import React, { useState, useEffect, useRef } from 'react';
import './Pricing.css';

const PLANS = [
  {
    name: 'Mart & POS Lite',
    icon: '🌱',
    desc: 'Single store, up to 5 users — ideal for one-location retail.',
    maxUsers: 200,
    highlight: false,
    components: [
      { label: 'CMS + POS (Single Store, 5 Users)', inr: 3600, usd: 37.9 },
      { label: 'Web + Mobile App', inr: 2945, usd: 31.0 },
      { label: 'Porter', inr: 2945, usd: 31.0 },
    ],
  },
  {
    name: 'Mart & POS Enterprise',
    icon: '🏢',
    desc: 'Multiple stores — pricing starting from, for chains & franchises.',
    maxUsers: 50,
    highlight: true,
    badge: 'Multi-Store',
    components: [
      { label: 'CMS + POS (Multiple Stores) — starting from', inr: 9975, usd: 105.0 },
      { label: 'Web + Mobile App', inr: 7600, usd: 80.0 },
      { label: 'Porter', inr: 7600, usd: 80.0 },
    ],
  },
];

export default function Pricing() {
  const [currency, setCurrency] = useState('inr');
  const ref = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) e.target.classList.add('visible'); },
      { threshold: 0.05 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const fmt = (n, cur) => cur === 'inr' ? `₹${n.toLocaleString('en-IN')}` : `$${n.toFixed(1)}`;

  return (
    <section id="pricing" style={{ background: '#ffffff', padding: '100px 0' }}>
      <div style={{ maxWidth: 1160, margin: '0 auto', padding: '0 40px' }}>

        {/* Section header */}
        <div ref={ref} className="reveal" style={{ marginBottom: 48, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 24 }}>
          <div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 11, fontWeight: 700, letterSpacing: '1.8px', textTransform: 'uppercase', color: '#2563eb', marginBottom: 18 }}>
              <span style={{ width: 18, height: 2, background: '#2563eb', borderRadius: 2, display: 'inline-block' }} />
              Pricing
            </div>
            <h2 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: 'clamp(32px,4vw,50px)', fontWeight: 800, lineHeight: 1.08, letterSpacing: '-1.5px', color: '#0c1220', marginBottom: 14 }}>
              Transparent, component-wise pricing.
            </h2>
            <p style={{ fontSize: 17, color: '#6b7280', lineHeight: 1.7, maxWidth: 560 }}>
              CMS + POS, Web + Mobile App, and Porter delivery app — priced per month, per store. Mix and match based on what you need.
            </p>
          </div>

          {/* Currency toggle */}
          <div className="pr-currency-toggle">
            <button className={`pr-toggle-btn ${currency === 'inr' ? 'active' : ''}`} onClick={() => setCurrency('inr')}>₹ INR</button>
            <button className={`pr-toggle-btn ${currency === 'usd' ? 'active' : ''}`} onClick={() => setCurrency('usd')}>$ USD</button>
          </div>
        </div>

        <div className="pr-grid">
          {PLANS.map(plan => (
            <div key={plan.name} className={`pr-card ${plan.highlight ? 'pr-card-highlight' : ''}`}>
              {plan.badge && <div className="pr-badge">{plan.badge}</div>}
              <div className="pr-icon">{plan.icon}</div>
              <div className="pr-name">{plan.name}</div>
              <div className="pr-desc">{plan.desc}</div>

              <div className="pr-price">
                <span className="pr-price-currency">{currency === 'inr' ? '₹' : '$'}</span>
                <span className="pr-price-amount">
                  {currency === 'inr' ? plan.components[0].inr.toLocaleString('en-IN') : plan.components[0].usd.toFixed(1)}
                </span>
                <span className="pr-price-period">/mo</span>
              </div>
              <div className="pr-price-note">Starting price for {plan.components[0].label}</div>

              <button
                className={`pr-cta ${plan.highlight ? 'pr-cta-primary' : 'pr-cta-outline'}`}
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Contact Us
              </button>

              <div className="pr-divider" />

              <ul className="pr-lineitems">
                {plan.components.map(c => (
                  <li key={c.label} className="pr-lineitem">
                    <span className="pr-lineitem-label">{c.label}</span>
                    <span className="pr-lineitem-price">{fmt(currency === 'inr' ? c.inr : c.usd, currency)}/mo</span>
                  </li>
                ))}
              </ul>

              <div className="pr-users">👥 Up to {plan.maxUsers} users included</div>
            </div>
          ))}
        </div>

        <div className="pr-footer">
          <div className="pr-footer-item">🔒 No hidden fees</div>
          <div className="pr-footer-item">📦 Free data migration assistance</div>
          <div className="pr-footer-item">💬 Custom quotes based on your scale</div>
          <div className="pr-footer-item">🌍 INR &amp; USD billing available</div>
        </div>

        <div className="pr-note">
          Prices shown are per month, per store, and exclude 18% GST. CMS + POS, Web + Mobile App, and Porter are billed as separate components. Enterprise pricing starts from the listed rate and depends on the number of stores and modules required. Contact us for a detailed quote.
        </div>

      </div>
    </section>
  );
}
